import React, { memo, useCallback, useMemo } from 'react';

import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles } from '@/hooks';
import { useFamilyActivitiesQuery } from '@/queries/use-family-activities-query';
import { useSessionStore } from '@/stores/session-store';
import { guardianColors } from '@/theme/colors';
import FamilyActivityList from './list';
import type { FamilyActivitySectionProps } from './types';
import { toFamilyActivityItem } from './utils';

const strings = STRINGS.guardian.home.familyActivity;

function FamilyActivitySection({ style }: FamilyActivitySectionProps) {
  const styles = useStyles();
  const uid = useSessionStore(s => s.user?.uid);
  const { data, isLoading, isRefetching, refetch } = useFamilyActivitiesQuery(uid);

  const resolvedItems = useMemo(() => {
    return (data?.activities ?? []).map(toFamilyActivityItem);
  }, [data]);

  const handleRefresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return (
    <Stack style={[styles.root, style]}>
      <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.title}>
        {strings.title}
      </Text>

      <Stack flex={1}>
        <FamilyActivityList
          items={resolvedItems}
          isLoading={isLoading}
          onRefresh={handleRefresh}
          isRefreshing={isRefetching}
        />
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: {
    flex: 1,
    paddingTop: scale(16),
    paddingRight: scale(20),
  },
  title: {
    color: guardianColors['10'],
    paddingBottom: scale(8),
  },
}));

export type { FamilyActivityItem, FamilyActivitySectionProps } from './types';
export default memo(FamilyActivitySection);
